import React, { useState } from 'react';
import { User, Users, UserPlus, Check } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { WizardStepProps } from '../../types/bookingWizard.types';
import { Dependent } from '../../types/client.types';

export const BeneficiaryStep: React.FC<WizardStepProps> = ({
    data,
    onUpdate,
    onNext,
    onBack
}) => {
    const [showNewDependentForm, setShowNewDependentForm] = useState(false);
    const [newDependentName, setNewDependentName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSelectClient = () => {
        setShowNewDependentForm(false);
        setNewDependentName('');
        onUpdate({
            isForDependent: false,
            dependent: null,
            newDependentName: ''
        });
    };

    const handleSelectDependent = (dependent: Dependent) => {
        setShowNewDependentForm(false);
        setNewDependentName('');
        onUpdate({
            isForDependent: true,
            dependent: dependent,
            newDependentName: ''
        });
    };
    
    const handleShowNewDependentForm = () => {
        setShowNewDependentForm(true);
        // Não atualiza isForDependent ainda - será atualizado após criar o dependente
    };

    const handleCreateNewDependent = async () => {
        if (!newDependentName || !data.client) {
            console.error('Missing data:', { newDependentName, client: data.client });
            return;
        }

        setLoading(true);
        setError('');
        
        console.log('Creating dependent:', { client_id: data.client.id, name: newDependentName });
        
        try {
            const result = await clientService.quickCreateDependent({
                client_id: data.client.id,
                name: newDependentName
            });
            
            console.log('Dependent created:', result);
            
            // Atualizar lista de dependentes do cliente
            const updatedClient = {
                ...data.client,
                dependents: [...(data.client.dependents || []), result.dependent]
            };
            
            onUpdate({
                client: updatedClient,
                isForDependent: true,
                dependent: result.dependent,
                newDependentName: ''
            });
            setShowNewDependentForm(false);
            setNewDependentName('');
        } catch (err: any) {
            console.error('Error creating dependent:', err);
            console.error('Error response:', err.response);
            setError(err.response?.data?.error || err.message || 'Erro ao criar dependente');
        } finally {
            setLoading(false);
        }
    };

    const dependents: Dependent[] = data.client?.dependents || [];

    return (
        <div className="step-container">
            <h3>Beneficiário</h3>
            <p>Para quem é este agendamento?</p>

            <div className="beneficiary-options">
                <div
                    className={`beneficiary-card ${!data.isForDependent && !showNewDependentForm ? 'selected' : ''}`}
                    onClick={handleSelectClient}
                >
                    <div className="beneficiary-icon">
                        <User size={32} />
                    </div>
                    <div className="beneficiary-info">
                        <h4>{data.client?.name}</h4>
                        <p>Cliente Principal</p>
                    </div>
                    {!data.isForDependent && !showNewDependentForm && <Check className="check-icon" />}
                </div>

                {dependents.length > 0 && (
                    <div className="dependents-section">
                        <h5>Dependentes</h5>
                        {dependents.map((dependent) => (
                            <div
                                key={dependent.id}
                                className={`beneficiary-card ${data.isForDependent && data.dependent?.id === dependent.id && !showNewDependentForm ? 'selected' : ''}`}
                                onClick={() => handleSelectDependent(dependent)}
                            >
                                <div className="beneficiary-icon">
                                    <Users size={24} />
                                </div>
                                <div className="beneficiary-info">
                                    <h4>{dependent.name}</h4>
                                    <p>Dependente</p>
                                </div>
                                {data.isForDependent && data.dependent?.id === dependent.id && !showNewDependentForm && (
                                    <Check className="check-icon" />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div
                    className={`beneficiary-card ${showNewDependentForm ? 'selected' : ''}`}
                    onClick={() => !showNewDependentForm && handleShowNewDependentForm()}
                    style={{ cursor: showNewDependentForm ? 'default' : 'pointer' }}
                >
                    <div className="beneficiary-icon">
                        <UserPlus size={24} />
                    </div>
                    <div className="beneficiary-info">
                        <h4>Novo Dependente</h4>
                        <p>Cadastrar novo dependente</p>
                    </div>
                    {showNewDependentForm && <Check className="check-icon" />}
                </div>

                {showNewDependentForm && (
                    <div className="new-dependent-form">
                        <input
                            type="text"
                            value={newDependentName}
                            onChange={(e) => setNewDependentName(e.target.value)}
                            placeholder="Nome do dependente"
                            autoFocus
                        />
                        <div className="form-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowNewDependentForm(false);
                                    setNewDependentName('');
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleCreateNewDependent}
                                disabled={loading || !newDependentName}
                            >
                                {loading ? 'Criando...' : 'Criar Dependente'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="step-navigation">
                <button className="btn-secondary" onClick={onBack}>
                    Voltar
                </button>
                <button
                    className="btn-primary"
                    onClick={onNext}
                    disabled={showNewDependentForm || (data.isForDependent === undefined && !data.dependent)}
                >
                    Continuar
                </button>
            </div>
        </div>
    );
};

export default BeneficiaryStep;
